import * as React from "react";
import classNames from "classnames";

import { toHumanRelativeDateFromNow } from "lib/utils/relative_time";
import { TenderStage } from "./landing_types";
import { formatAmount } from "./landing_utils";
import RecordStage from "./RecordStage";

import css from "./RecordCardShelf.module.scss";

import BANK from "../../../assets/images/icons/bank.svg";
import TAG_ICON from "../../../assets/images/icons/tag.svg";

const CARD_WIDTH = 400;

// TODO: consolidate with other types?
export type SimilarRecord = {
  name: string;
  guid: string;
  buyers: {
    name: string;
  }[];
  stage: TenderStage;
  value?: number;
  currency?: string;
  publish_date: string;
  close_date?: string;
  href: string;
};

function RecordCardShelf({
  records,
  description,
  wrapperClassName,
}: {
  records: SimilarRecord[];
  description: string;
  wrapperClassName?: string;
}) {
  const shelfElem = React.useRef<HTMLUListElement | null>(null);
  const [prevDisabled, setPrevDisabled] = React.useState(true);
  const [nextDisabled, setNextDisabled] = React.useState(false);

  const onNavigate = React.useCallback((offset: number) => {
    if (shelfElem.current) {
      shelfElem.current.scrollLeft += offset;
    }
  }, []);

  const onScroll = React.useCallback((event: React.UIEvent<HTMLUListElement>) => {
    const elem = event.currentTarget;

    setPrevDisabled(elem.scrollLeft === 0);
    setNextDisabled(elem.scrollWidth === elem.clientWidth + elem.scrollLeft);
  }, []);

  return (
    <div className={wrapperClassName}>
      <div className={css.controlsContainer}>
        <p>{description}</p>
        <div>
          <button
            onClick={() => onNavigate(-CARD_WIDTH)}
            disabled={prevDisabled}
            className={css.navigationBtn}
          >
            &lt;
          </button>
          <button
            onClick={() => onNavigate(CARD_WIDTH)}
            disabled={nextDisabled}
            className={css.navigationBtn}
          >
            &gt;
          </button>
        </div>
      </div>
      <ul className={css.shelf} onScroll={onScroll} ref={shelfElem}>
        {records.map((r, i) => (
          <li key={i}>
            <Card record={r} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function Card({ record }: { record: SimilarRecord }) {
  // It is a deliberate design-decision (for SEO purposes), so that the actual text of the link is
  // the record title; search engines place particular emphasis on the human-readble content of
  // anchor tags.
  //
  // And given that nested anchors are not allowed in HTML, the other anchor is synthetic (with this
  // onClick event handler)
  const handleNavigation = React.useCallback(() => {
    window.location.pathname = record.href;
  }, [record.href]);

  return (
    <div className={css.card} style={{ width: CARD_WIDTH }} onClick={handleNavigation}>
      <RecordStage record={record} />
      <h4>
        <a
          href={record.href}
          title={record.name}
          className={css.cardTitle}
          data-id="explore_articles"
        >
          {record.name}
        </a>
      </h4>
      <div className={classNames(css.iconDetail, css.buyer)} title={record.buyers[0].name}>
        <img height={14} width={14} src={BANK} alt="bank" />
        {
          // TODO: handle multiple buyers
          record.buyers[0].name
        }
      </div>
      {!!record.value && record.currency && (
        <div className={css.iconDetail}>
          <img height={14} width={14} src={TAG_ICON} alt="price-tag-icon" />

          {formatAmount(record.value, record.currency)}
        </div>
      )}
      <p className={css.publishDate}>Published {toHumanRelativeDateFromNow(record.publish_date)}</p>
    </div>
  );
}

export default RecordCardShelf;
